import { useState } from 'react';
import type { AxiosError } from 'axios';
import { createOrganization } from '@api/backoffice';
import type { CreatedOrganization } from '@api/backoffice';
import {
  DialogOverlay,
  DialogContent,
  DialogTitle,
  FormField,
  FormLabel,
  FormInput,
  ButtonRow,
  ActionButton,
  WarningBanner,
} from '@components/Backoffice/BackofficeLayout.styled';
import {
  ConfirmCheckbox,
  ConfirmRow,
  CredentialField,
  CredentialValue,
  ErrorText,
  HelperText,
} from './CreateOrgDialog.styled';

interface CreateOrgDialogProps {
  readonly onClose: () => void;
  readonly onCreated: (organization: CreatedOrganization) => void;
}

const MAX_NAME_LENGTH = 100;

function serverErrorMessage(err: unknown): string | null {
  const ax = err as AxiosError<{ error?: string; message?: string }> | undefined;
  if (ax?.isAxiosError !== true) return null;
  const body = ax.response?.data;
  const message = body?.error ?? body?.message;
  return typeof message === 'string' && message.length > 0 ? message : null;
}

const CreateOrgDialog: React.FC<CreateOrgDialogProps> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [enableCamAccess, setEnableCamAccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedOrganization | null>(null);
  const [storedInLastPass, setStoredInLastPass] = useState(false);

  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0 && trimmedName.length <= MAX_NAME_LENGTH;

  const handleSubmit = async () => {
    if (!isNameValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const organization = await createOrganization(trimmedName, enableCamAccess);
      setCreated(organization);
      onCreated(organization);
    } catch (err) {
      setError(serverErrorMessage(err) ?? 'Failed to create the organisation');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (value: string) => {
    navigator.clipboard?.writeText(value).catch(() => undefined);
  };

  if (created) {
    return (
      <DialogOverlay>
        <DialogContent>
          <DialogTitle>{created.name} created</DialogTitle>
          <WarningBanner>
            This password is shown once and cannot be recovered. Store it in LastPass
            before closing this dialog.
          </WarningBanner>

          <FormField>
            <FormLabel>Superadmin account</FormLabel>
            <CredentialField>
              <CredentialValue>{created.superadminEmail}</CredentialValue>
              <ActionButton
                $variant="ghost"
                aria-label="Copy superadmin email"
                onClick={() => copy(created.superadminEmail)}
              >
                Copy
              </ActionButton>
            </CredentialField>
          </FormField>

          <FormField>
            <FormLabel>One-time password</FormLabel>
            <CredentialField>
              <CredentialValue>{created.superadminPassword}</CredentialValue>
              <ActionButton
                $variant="ghost"
                aria-label="Copy superadmin password"
                onClick={() => copy(created.superadminPassword)}
              >
                Copy
              </ActionButton>
            </CredentialField>
          </FormField>

          <HelperText>
            {created.camEnabled
              ? 'CAM access is enabled for this superadmin. Their CAM organisation is created on their first CAM login.'
              : 'CAM access is not enabled. Grant the CAM skill from the organisation page when the customer needs it.'}
          </HelperText>

          <ConfirmRow>
            <ConfirmCheckbox
              type="checkbox"
              checked={storedInLastPass}
              onChange={(e) => setStoredInLastPass(e.target.checked)}
            />
            I confirm I added the password in LastPass
          </ConfirmRow>

          <ButtonRow>
            <ActionButton
              $variant="primary"
              onClick={onClose}
              disabled={!storedInLastPass}
            >
              Close
            </ActionButton>
          </ButtonRow>
        </DialogContent>
      </DialogOverlay>
    );
  }

  return (
    // Dismissing mid-flight would unmount the dialog while the request is still
    // running: the organisation still gets created, but its one-time password
    // dies with the component and can only be replaced from Cognito.
    <DialogOverlay onClick={submitting ? undefined : onClose}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogTitle>Add organisation</DialogTitle>
        <HelperText>
          A superadmin account is created for the organisation and its password is shown
          once. The name must be unique across Aibuild OS and CAM.
        </HelperText>

        {error && <ErrorText role="alert">{error}</ErrorText>}

        <FormField>
          <FormLabel>Organisation name</FormLabel>
          <FormInput
            aria-label="Organisation name"
            value={name}
            maxLength={MAX_NAME_LENGTH}
            disabled={submitting}
            onChange={(e) => setName(e.target.value)}
          />
        </FormField>

        <ConfirmRow>
          <ConfirmCheckbox
            type="checkbox"
            checked={enableCamAccess}
            disabled={submitting}
            onChange={(e) => setEnableCamAccess(e.target.checked)}
          />
          Enable CAM access for the superadmin
        </ConfirmRow>

        <ButtonRow>
          <ActionButton $variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </ActionButton>
          <ActionButton
            $variant="primary"
            onClick={handleSubmit}
            disabled={submitting || !isNameValid}
          >
            {submitting ? 'Creating...' : 'Create organisation'}
          </ActionButton>
        </ButtonRow>
      </DialogContent>
    </DialogOverlay>
  );
};

export default CreateOrgDialog;
